import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Plus, Edit2, Power, Loader2, MapPin, Download } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { CityForm } from './CityForm';

interface City { id: number; name?: string; stateId?: number; isMetro?: boolean; isActive: boolean; state?: { id: number; name: string } | null }
interface State { id: number; name: string }

export function CitiesPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [stateId, setStateId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<City | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get<State[]>('/states')).data,
    staleTime: 1000 * 60 * 60,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['cities', { search, stateId, page }],
    queryFn: async () => {
      const params: any = { page, pageSize };
      if (search) params.search = search;
      if (stateId) params.stateId = stateId;
      return (await api.get<{ items: City[]; total: number }>('/cities', { params })).data;
    },
    placeholderData: (prev: any) => prev,
  });
  const toggle = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/cities/${id}/toggle`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cities'] }); toast.success('Status updated'); },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 min-w-[260px]">
            <Search size={14} className="text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search city name..."
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <select value={stateId} onChange={(e) => { setStateId(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary min-w-[200px]">
            <option value="">All states</option>
            {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={() => downloadBlob(`/cities/export?${new URLSearchParams({ ...(search ? { search } : {}), ...(stateId ? { stateId } : {}) }).toString()}`, `cities-${new Date().toISOString().slice(0,10)}.csv`)}
          className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
          <Download size={14} /> Export
        </button>
        <button onClick={() => setShowCreate(true)} className="bg-primary text-white px-3 py-2 text-sm rounded-lg hover:bg-primary-700 flex items-center gap-2">
          <Plus size={14} /> Add City
        </button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 text-xs text-slate-500 flex justify-between">
          <span>Total: <b>{total.toLocaleString()}</b> cities</span>
          <span>
            {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 disabled:opacity-30">‹</button>
            <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-2 disabled:opacity-30">›</button>
          </span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-20">ID</th>
              <th className="text-left px-3 py-2">City</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Metro</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-center py-8 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400"><MapPin size={20} className="mx-auto mb-1 opacity-50" /> No cities found.</td></tr>
            )}
            {items.map(c => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.id}</td>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-slate-600">{c.state?.name ?? '—'}</td>
                <td className="px-3 py-2">{c.isMetro ? <span className="text-xs px-2 py-0.5 bg-violet-50 text-violet-700 rounded">Metro</span> : '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${c.isActive ? 'text-success' : 'text-slate-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setEditing(c)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded"><Edit2 size={14} /></button>
                  <button onClick={() => toggle.mutate(c.id)}
                    className={`p-1.5 rounded ${c.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-success hover:bg-success/10'}`}>
                    <Power size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add City" maxWidth="md">
        <CityForm onSuccess={() => setShowCreate(false)} />
      </Modal>
      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit City" subtitle={editing?.name} maxWidth="md">
        {editing && <CityForm cityId={editing.id} defaultValues={editing} onSuccess={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}
